// ADR-0153 (fase 3, corte 1, #0750) — las encuestas del propietario.
//
// Tres encuestas, una por hito:
//   - `nps_2m` / `nps_9m`: «¿Qué tan probable es que nos recomiendes?»
//     (0–10) a los 2 y a los 9 meses de la entrega. El ancla es
//     `assets.attributes.fecha_entrega` YA PASADA (decisión del founder,
//     2026-10-16: el acta digital no existe todavía). Sólo la recibe el
//     PRIMER dueño de la unidad: si el dueño cambió, la ola no sale.
//   - `visita`: «¿Cómo fue la visita técnica?» (1–5), al dar el
//     propietario la conformidad «conforme» de una observación.
//
// Este módulo es la única definición de las reglas puras: los tipos, las
// escalas, el vencimiento de cada ola y la fórmula del NPS. core-api
// decide cuándo escribir; el wire deriva de aquí (fitness `wire-enums`).
import { z } from "zod";
import { addCalendarDays, addCalendarMonths } from "./observation-closure-clock.js";
export const OWNER_SURVEY_KINDS = ["nps_2m", "nps_9m", "visita"];
export const ownerSurveyKindSchema = z.enum(OWNER_SURVEY_KINDS);
/** Las dos olas del NPS, en orden. */
export const OWNER_SURVEY_NPS_KINDS = [
    "nps_2m",
    "nps_9m",
];
export const ownerSurveyNpsKindSchema = z.enum(OWNER_SURVEY_NPS_KINDS);
/** Meses de calendario desde la entrega de cada ola (estándar británico:
 *  a las 8 semanas y a los 9 meses; aquí, 2 meses de calendario). */
export const OWNER_SURVEY_NPS_MONTHS = {
    nps_2m: 2,
    nps_9m: 9,
};
export function isNpsSurveyKind(kind) {
    return kind === "nps_2m" || kind === "nps_9m";
}
/** La escala de cada tipo: el NPS de 0 a 10, la visita de 1 a 5. */
export const OWNER_SURVEY_SCALE = {
    nps: { min: 0, max: 10 },
    visita: { min: 1, max: 5 },
};
export function ownerSurveyScale(kind) {
    return isNpsSurveyKind(kind) ? OWNER_SURVEY_SCALE.nps : OWNER_SURVEY_SCALE.visita;
}
/** ¿Es un puntaje válido para ese tipo? Entero y dentro de la escala. */
export function ownerSurveyScoreInRange(kind, score) {
    const { min, max } = ownerSurveyScale(kind);
    return Number.isInteger(score) && score >= min && score <= max;
}
/** El largo máximo del comentario opcional (CHECK de `owner_surveys`). */
export const OWNER_SURVEY_COMMENT_MAX = 1000;
/**
 * La ventana de recuperación, en días corridos desde el vencimiento: una
 * ola que venció hace más que esto no sale. Junto con el piso de la
 * activación de la org, es lo que impide que encender el job mande las
 * dos olas a todo el stock ya entregado (sin backfill, decisión 6 del
 * founder). También cubre un tick perdido o un propietario que activa su
 * cuenta unos días después.
 */
export const OWNER_SURVEY_RECOVERY_DAYS = 30;
/** El vencimiento de una ola: el día de entrega + N meses de calendario. */
export function npsDueOn(deliveryDay, months) {
    return addCalendarMonths(deliveryDay, months);
}
/**
 * ¿La ola sale hoy? Sí si venció (`due ≤ hoy`), si venció después de que
 * la org activó las encuestas (`due ≥ activación`) y si no pasó la
 * ventana de recuperación (`hoy ≤ due + 30`). Todas son fechas
 * «AAAA-MM-DD» de Santiago, que ordenan como texto.
 */
export function npsWaveDue(input) {
    const due = npsDueOn(input.deliveryDay, OWNER_SURVEY_NPS_MONTHS[input.kind]);
    if (due > input.today)
        return null;
    if (due < input.activationDay)
        return null;
    if (input.today > addCalendarDays(due, OWNER_SURVEY_RECOVERY_DAYS))
        return null;
    return due;
}
// ─── agregados del dashboard ──────────────────────────────────────────
/** Bajo este número de respuestas no se muestra un número (ni NPS ni
 *  promedio): la muestra no alcanza y, en el promedio por contratista,
 *  sería casi identificar una respuesta. */
export const OWNER_SURVEY_MIN_N = 5;
/** 9–10 promotor, 7–8 pasivo, 0–6 detractor. */
export function npsBucket(score) {
    if (score >= 9)
        return "promotor";
    if (score >= 7)
        return "pasivo";
    return "detractor";
}
/** NPS = 100·(promotores − detractores)/respondidas, redondeado; null
 *  con menos de `OWNER_SURVEY_MIN_N` respuestas. */
export function npsScore(counts) {
    if (counts.respondidas < OWNER_SURVEY_MIN_N)
        return null;
    return Math.round((100 * (counts.promotores - counts.detractores)) / counts.respondidas);
}
/** Promedio con un decimal; null con menos de `OWNER_SURVEY_MIN_N`. */
export function surveyAverage(sum, n) {
    if (n < OWNER_SURVEY_MIN_N)
        return null;
    return Math.round((sum / n) * 10) / 10;
}
//# sourceMappingURL=owner-surveys.js.map